// CareLogix™ ORACLE — ORACLE SIGNAL ENGINE™ Command Center (Phase 32A)
// Strictly additive. Dark green/gold theme preserved. Compliance Lock™ visible.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { PALETTE, scoreColor } from "@/components/animation/MotionShared";
import { signalApi } from "@/api/signalClient";

const UNIVERSE_OPTIONS = [
  { id: "stocks",       label: "Equities" },
  { id: "forex",        label: "Forex" },
  { id: "crypto",       label: "Crypto" },
  { id: "healthcare",   label: "Healthcare" },
  { id: "startups",     label: "Startups" },
  { id: "grants",       label: "Grants" },
  { id: "commodities",  label: "Commodities" },
  { id: "africa",       label: "Africa" },
  { id: "fixed_income", label: "Fixed Income" },
  { id: "indices",      label: "Indices" },
];

function NisiCell({ label, value, color, testid }) {
  const c = color || scoreColor(value);
  const w = Math.max(2, Math.min(100, Number(value) || 0));
  return (
    <div style={S.cell} data-testid={testid}>
      <div style={S.cellHead}>
        <span style={S.cellLbl}>{label}</span>
        <span style={{ ...S.cellVal, color: c }}>{Math.round(Number(value) || 0)}</span>
      </div>
      <div style={S.cellBar}>
        <motion.div
          style={{ ...S.cellBarFill, background: c }}
          initial={{ width: "0%" }}
          animate={{ width: `${w}%` }}
          transition={{ duration: 0.9, ease: "easeOut" }}
        />
      </div>
    </div>
  );
}

function Detector({ d }) {
  const c = scoreColor(d.strength);
  return (
    <li style={S.detRow} data-testid={`p32-signal-detector-${d.detector}`}>
      <span style={{ ...S.detIcon, color: c }}>{d.icon || "•"}</span>
      <span style={S.detLabel}>{d.label}</span>
      <span style={{ ...S.detDir, color: PALETTE.slate4 }}>{d.direction}</span>
      <span style={{ ...S.detStrength, color: c }}>{d.strength}</span>
    </li>
  );
}

function SignalCard({ s }) {
  const verdictColor = scoreColor(s.nisi.opportunity_score);
  const directionColor = s.direction === "bullish" ? PALETTE.greenAccent : s.direction === "bearish" ? PALETTE.red : PALETTE.slate4;
  const verdictLabel = (s.conviction || "hold").replace("_", " ").toUpperCase();
  return (
    <motion.article
      style={S.card}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      data-testid={`p32-signal-card-${s.symbol}`}
    >
      <header style={S.cardHead}>
        <div style={S.cardHeadLeft}>
          <span style={S.cardSym} data-testid={`p32-signal-card-${s.symbol}-symbol`}>{s.symbol}</span>
          <span style={S.cardUni}>{s.universe_label}</span>
        </div>
        <div style={S.cardHeadRight}>
          <span style={{ ...S.cardDir, color: directionColor, borderColor: directionColor }} data-testid={`p32-signal-card-${s.symbol}-direction`}>
            {s.direction.toUpperCase()}
          </span>
          <span style={{ ...S.cardVerdict, color: verdictColor, borderColor: verdictColor }} data-testid={`p32-signal-card-${s.symbol}-conviction`}>
            {verdictLabel}
          </span>
        </div>
      </header>

      <div style={S.cardGrid} data-testid={`p32-signal-card-${s.symbol}-nisi`}>
        <NisiCell label="Opportunity" value={s.nisi.opportunity_score}                                testid={`p32-signal-nisi-opp-${s.symbol}`} />
        <NisiCell label="Risk"        value={s.nisi.risk_score}        color={PALETTE.red}            testid={`p32-signal-nisi-risk-${s.symbol}`} />
        <NisiCell label="Confidence"  value={s.nisi.confidence_score}                                  testid={`p32-signal-nisi-conf-${s.symbol}`} />
        <NisiCell label="Volatility"  value={s.nisi.volatility_score}  color={PALETTE.amber}          testid={`p32-signal-nisi-vol-${s.symbol}`} />
        <NisiCell label="Trend"       value={s.nisi.trend_strength}                                    testid={`p32-signal-nisi-trend-${s.symbol}`} />
        <NisiCell label="Probability" value={s.nisi.probability_score}                                 testid={`p32-signal-nisi-prob-${s.symbol}`} />
        <NisiCell label="Catalyst"    value={s.nisi.catalyst_score}    color={PALETTE.goldBright}     testid={`p32-signal-nisi-cat-${s.symbol}`} />
        <NisiCell label="Liquidity"   value={s.nisi.liquidity_score}   color={PALETTE.emerald}        testid={`p32-signal-nisi-liq-${s.symbol}`} />
      </div>

      <details style={S.details}>
        <summary style={S.detailsSummary} data-testid={`p32-signal-card-${s.symbol}-detectors-toggle`}>
          9 detectors · click for breakdown
        </summary>
        <ul style={S.detList} data-testid={`p32-signal-card-${s.symbol}-detectors`}>
          {(s.detectors || []).map((d) => <Detector d={d} key={d.detector} />)}
        </ul>
      </details>

      <div style={S.cardAction} data-testid={`p32-signal-card-${s.symbol}-action`}>
        <strong style={{ color: PALETTE.gold }}>Action:</strong> {s.action}
      </div>

      {Array.isArray(s.risk_notes) && s.risk_notes.length > 0 && (
        <ul style={S.notes} data-testid={`p32-signal-card-${s.symbol}-risk-notes`}>
          {s.risk_notes.map((n, i) => <li key={i} style={S.note}>⚠ {n}</li>)}
        </ul>
      )}

      <footer style={S.cardFoot}>
        <span style={S.cardCompliance}>Research only · Not financial advice · Past performance not predictive · HITL required.</span>
      </footer>
    </motion.article>
  );
}

export default function SignalCommandCenter({ testid = "p32-signal-command" }) {
  const [universe, setUniverse] = useState("stocks");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async (u) => {
    setLoading(true);
    setError(null);
    try {
      const res = await signalApi.scan(u, 8);
      setData(res.data);
    } catch (e) {
      setError("Could not load signals — retrying may help.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(universe); }, [universe, load]);

  const signals = useMemo(() => (data && Array.isArray(data.signals)) ? data.signals : [], [data]);

  return (
    <section style={S.shell} data-testid={testid}>
      <header style={S.header}>
        <div>
          <h2 style={S.title} data-testid={`${testid}-title`}>ORACLE SIGNAL ENGINE™</h2>
          <p style={S.subtitle} data-testid={`${testid}-subtitle`}>
            Opportunity discovery across {UNIVERSE_OPTIONS.length} universes — advisory only, compliance-locked, HITL gated.
          </p>
        </div>
        <span style={S.complianceBadge} data-testid={`${testid}-compliance-badge`}>COMPLIANCE LOCK™</span>
      </header>

      <div style={S.universeRow} data-testid={`${testid}-universe-selector`}>
        {UNIVERSE_OPTIONS.map((u) => (
          <button
            key={u.id}
            type="button"
            onClick={() => setUniverse(u.id)}
            style={{ ...S.uniBtn, ...(universe === u.id ? S.uniBtnActive : null) }}
            data-testid={`${testid}-universe-${u.id}`}
            aria-pressed={universe === u.id}
          >
            {u.label}
          </button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        {loading && (
          <motion.div key="loading" style={S.loading} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} data-testid={`${testid}-loading`}>
            Scanning {universe}…
          </motion.div>
        )}
        {!loading && error && (
          <motion.div key="err" style={S.err} initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid={`${testid}-error`}>
            {error}
          </motion.div>
        )}
        {!loading && !error && signals.length === 0 && (
          <motion.div key="empty" style={S.loading} initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid={`${testid}-empty`}>
            No signals in this universe right now.
          </motion.div>
        )}
      </AnimatePresence>

      {!loading && signals.length > 0 && (
        <div style={S.cards} data-testid={`${testid}-cards`}>
          {signals.map((s) => <SignalCard s={s} key={s.signal_id || s.symbol} />)}
        </div>
      )}

      <div style={S.footnote} data-testid={`${testid}-footnote`}>
        {data?.disclaimer || "Research only. Not financial advice. Past performance not predictive."}
      </div>
    </section>
  );
}

const S = {
  shell:        { background: "linear-gradient(180deg, rgba(8,32,22,0.55), rgba(4,6,8,0.85))", border: `1px solid ${PALETTE.goldGlow}`, borderRadius: 18, padding: 18, display: "flex", flexDirection: "column", gap: 14, color: PALETTE.slate3 },
  header:       { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" },
  title:        { margin: 0, color: PALETTE.goldBright, fontSize: 18, letterSpacing: "0.07em" },
  subtitle:     { margin: "4px 0 0 0", color: PALETTE.slate4, fontSize: 12.5, lineHeight: 1.5 },
  complianceBadge: { color: PALETTE.amber, border: `1px solid ${PALETTE.amber}`, padding: "3px 9px", borderRadius: 999, fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: "0.12em", background: "rgba(252,211,77,0.06)" },
  universeRow:  { display: "flex", flexWrap: "wrap", gap: 6 },
  uniBtn:       { appearance: "none", background: "transparent", border: `1px solid ${PALETTE.goldGlow}`, color: PALETTE.slate3, padding: "6px 11px", borderRadius: 999, cursor: "pointer", fontFamily: "'Space Mono', monospace", fontSize: 11.5, letterSpacing: "0.06em" },
  uniBtnActive: { color: PALETTE.bgDeep, background: PALETTE.gold, borderColor: PALETTE.gold, fontWeight: 800 },
  cards:        { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 12 },
  card:         { background: "linear-gradient(135deg, rgba(11,15,20,0.95), rgba(8,32,22,0.85))", border: `1px solid ${PALETTE.goldGlow}`, borderRadius: 14, padding: 12, display: "flex", flexDirection: "column", gap: 10 },
  cardHead:     { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" },
  cardHeadLeft: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" },
  cardHeadRight:{ display: "flex", alignItems: "center", gap: 6 },
  cardSym:      { color: PALETTE.goldBright, fontSize: 17, letterSpacing: "0.04em", fontWeight: 700 },
  cardUni:      { color: PALETTE.gold, fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: "0.10em", border: `1px solid ${PALETTE.goldGlow}`, padding: "2px 7px", borderRadius: 999, background: "rgba(197,160,89,0.06)" },
  cardDir:      { fontFamily: "'Space Mono', monospace", fontSize: 10, fontWeight: 800, letterSpacing: "0.10em", padding: "3px 8px", borderRadius: 999, border: "1px solid" },
  cardVerdict:  { fontFamily: "'Space Mono', monospace", fontSize: 10, fontWeight: 800, letterSpacing: "0.10em", padding: "3px 8px", borderRadius: 999, border: "1px solid" },
  cardGrid:     { display: "grid", gridTemplateColumns: "repeat(2, minmax(0,1fr))", gap: 6 },
  cell:         { background: "#040608", border: "1px solid rgba(255,255,255,0.05)", borderRadius: 8, padding: 6, display: "flex", flexDirection: "column", gap: 4 },
  cellHead:     { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 },
  cellLbl:      { color: PALETTE.gold, fontFamily: "'Space Mono', monospace", fontSize: 9.5, letterSpacing: "0.10em" },
  cellVal:      { fontFamily: "'Space Mono', monospace", fontSize: 12.5, fontWeight: 800 },
  cellBar:      { height: 4, background: "rgba(255,255,255,0.06)", borderRadius: 2, overflow: "hidden" },
  cellBarFill:  { height: "100%", width: 0 },
  details:      { background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.05)", borderRadius: 8, padding: "6px 8px" },
  detailsSummary:{ color: PALETTE.gold, fontSize: 11.5, cursor: "pointer", listStyle: "none", letterSpacing: "0.06em" },
  detList:      { margin: "6px 0 0 0", padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 4 },
  detRow:       { display: "grid", gridTemplateColumns: "18px 1fr auto auto", alignItems: "center", gap: 8, fontSize: 11.5, padding: "2px 0" },
  detIcon:      { fontFamily: "'Space Mono', monospace", fontSize: 13, width: 18, textAlign: "center" },
  detLabel:     { color: PALETTE.slate3, fontSize: 11.5 },
  detDir:       { fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: "0.06em", textTransform: "uppercase" },
  detStrength:  { fontFamily: "'Space Mono', monospace", fontSize: 11.5, fontWeight: 800, minWidth: 24, textAlign: "right" },
  cardAction:   { fontSize: 12.5, lineHeight: 1.5, padding: "6px 10px", background: "rgba(197,160,89,0.05)", border: `1px solid ${PALETTE.goldGlow}`, borderRadius: 8, color: PALETTE.slate3 },
  notes:        { margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 4 },
  note:         { color: PALETTE.amber, fontSize: 11.5, lineHeight: 1.4 },
  cardFoot:     { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" },
  cardCompliance:{ color: PALETTE.slate5, fontFamily: "'Space Mono', monospace", fontSize: 9.5 },
  loading:      { color: PALETTE.slate4, fontSize: 13, padding: "20px 0" },
  err:          { color: PALETTE.red, fontSize: 13, padding: "20px 0" },
  footnote:     { color: PALETTE.slate5, fontSize: 10.5, fontStyle: "italic", textAlign: "right" },
};
